// The grid of the *Unassigned faces* tab (PRD-0031 slice 04). Pure: it renders
// what it is given and reports the ticks; the fetch lives in the feature hook.
// A recording's passes come as one card per cluster (PRD-0074 slice 04): the
// best shot, how many passes it stands for, their span, and "Play pass".
import { Check, Play, Video } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { FaceTile } from '@/components/faces/FaceTile'
import { facePageNumber } from '@/components/faces/format'
import { QualityFlagBadges } from '@/components/faces/QualityFlagBadges'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import type { UnassignedFace } from '@/services/api/identities'
import { cn } from '@/utils/cn'
import { formatVideoTime } from '@/utils/video-file'

interface UnassignedFacesGridProps {
  faces: UnassignedFace[]
  isLoading: boolean
  isSelected: (faceId: string) => boolean
  onToggle: (faceId: string) => void
  /**
   * "Play pass" on a card of a recording: open the recording at that pass. Omitted where no
   * player can be opened; a photo's card never shows the control.
   */
  onPlayPass?: (face: UnassignedFace) => void
}

/** How many passes a card stands for: the cluster's, 1 for a photo's face or an older API. */
export const passCount = (face: UnassignedFace): number => face.cluster_size ?? 1

function FaceCard({
  face,
  selected,
  onToggle,
  onPlayPass,
}: {
  face: UnassignedFace
  selected: boolean
  onToggle: (faceId: string) => void
  onPlayPass?: (face: UnassignedFace) => void
}) {
  const { t } = useTranslation()
  const source = face.document_name || t('identity.faces.unknownSource')
  // A pass of a recording (ADR-0085) is placed by its time, not by a page; a cluster by its span.
  const isVideo = face.source === 'video' && face.t_start !== null
  const passes = passCount(face)
  const videoTime = isVideo ? formatVideoTime(face.t_start as number) : null
  const videoSpan =
    isVideo && passes > 1 && face.t_end != null
      ? t('identity.unassigned.videoSpan', {
          from: videoTime,
          to: formatVideoTime(face.t_end),
        })
      : null
  // What a screen reader gets: the span of a cluster, or the moment of one pass.
  const videoAt =
    videoTime === null
      ? null
      : videoSpan !== null && face.t_end != null
        ? t('identity.unassigned.videoBetween', {
            from: videoTime,
            to: formatVideoTime(face.t_end),
          })
        : t('identity.unassigned.videoAt', { time: videoTime })
  // A PDF's face names its page; a photo or a recording has none to name.
  const page = facePageNumber(face.page, face.document_name)
  const sourceLabel =
    page === null ? source : t('identity.photos.pageLabel', { name: source, page })

  return (
    <div className="relative">
      <Button
        type="button"
        variant="ghost"
        aria-pressed={selected}
        onClick={() => onToggle(face.face_id)}
        data-cluster-size={passes}
        className={cn(
          'h-auto w-full flex-col items-stretch gap-2 rounded-lg border p-3 text-left',
          selected ? 'border-primary bg-primary/5' : 'border-border bg-card'
        )}
      >
        <div className="relative self-center">
          <FaceTile url={face.crop_url} alt={sourceLabel} size="lg" />
          {selected ? (
            <span className="absolute -right-1 -top-1 flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground max-md:z-20">
              <Check className="size-3" />
            </span>
          ) : null}
          {passes > 1 ? (
            /* The count sits on the shot: a card that stands for thirty passes says so first. */
            <Badge
              variant="secondary"
              className="absolute -bottom-1 -left-1 px-1.5 py-0 text-[10px] font-medium tabular-nums max-md:z-20"
              data-pass-count={passes}
            >
              {t('identity.unassigned.passes', { count: passes })}
            </Badge>
          ) : null}
        </div>
        <p className="truncate text-xs font-medium text-foreground" title={sourceLabel}>
          {sourceLabel}
        </p>
        {videoAt !== null ? (
          <p
            className="flex items-center gap-1 text-xs tabular-nums text-muted-foreground"
            title={videoAt}
          >
            <Video className="size-3 shrink-0" aria-hidden />
            <span aria-hidden>{videoSpan ?? videoTime}</span>
            <span className="sr-only">{videoAt}</span>
          </p>
        ) : null}
        <div className="flex flex-wrap gap-1">
          <QualityFlagBadges flags={face.quality_flags} />
          {face.pending_suggestions > 0 ? (
            <Badge variant="secondary" className="px-1 py-0 text-[10px] font-normal">
              {t('identity.faces.suggestedCount', { count: face.pending_suggestions })}
            </Badge>
          ) : null}
        </div>
      </Button>
      {isVideo && onPlayPass ? (
        /* Beside the card, not inside it: the card is one button, the control another. */
        <Button
          type="button"
          size="sm"
          variant="outline"
          aria-label={t('documents.detail.faces.playPass')}
          title={t('documents.detail.faces.playPass')}
          data-play-pass={face.face_id}
          className="absolute right-2 top-2 size-8 p-0"
          onClick={() => onPlayPass(face)}
        >
          <Play className="size-4" aria-hidden />
        </Button>
      ) : null}
    </div>
  )
}

export function UnassignedFacesGrid({
  faces,
  isLoading,
  isSelected,
  onToggle,
  onPlayPass,
}: UnassignedFacesGridProps) {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {Array.from({ length: 8 }, (_, index) => (
          <Skeleton key={`face-skeleton-${index}`} className="h-40 w-full rounded-lg" />
        ))}
      </div>
    )
  }

  if (faces.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border py-12 text-center text-sm text-muted-foreground">
        {t('identity.unassigned.empty')}
      </p>
    )
  }

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
      {faces.map(face => (
        <FaceCard
          key={face.face_id}
          face={face}
          selected={isSelected(face.face_id)}
          onToggle={onToggle}
          onPlayPass={onPlayPass}
        />
      ))}
    </div>
  )
}
